import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Plus, Layers, Edit2, Trash2, Users, X, Check,
  ChevronLeft, ChevronRight, AlertCircle,ChevronDown
} from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const COLOR_SWATCHES = [
  "#83bbe1", "#3b82f6", "#10b981", "#8b5cf6", "#ec4899",
  "#f59e0b", "#ef4444", "#64748b"
];

const COLOR_NAMES = {
  "#83bbe1": "Sky Blue",
  "#3b82f6": "Blue",
  "#10b981": "Green",
  "#8b5cf6": "Purple",
  "#ec4899": "Pink",
  "#f59e0b": "Amber",
  "#ef4444": "Red",
  "#64748b": "Slate"
};

const channelOptions = [
  { value: "", label: "All Channels" },
  { value: "email", label: "Email" },
  { value: "freshchat", label: "Freshchat" },
  { value: "freshdesk", label: "Freshdesk" },
  { value: "slack", label: "Slack" },
  { value: "telegram", label: "Telegram" },
  { value: "webchat", label: "Webchat" },
  { value: "webhook", label: "Webhook" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "widget", label: "Widget Chat" },
];

function getChannelBadge(chan) {
  const c = String(chan || "widget").toLowerCase();
  const map = {
    webchat: ["Webchat", "bg-indigo-50 text-indigo-600 border border-indigo-100/50"],
    whatsapp: ["WhatsApp", "bg-emerald-50 text-emerald-600 border border-emerald-100/50"],
    telegram: ["Telegram", "bg-sky-50 text-sky-600 border border-sky-100/50"],
    slack: ["Slack", "bg-purple-50 text-purple-600 border border-purple-100/50"],
    email: ["Email", "bg-amber-50 text-amber-600 border border-amber-100/50"],
    freshdesk: ["Freshdesk", "bg-emerald-50 text-emerald-600 border border-emerald-100/50"],
    freshchat: ["Freshchat", "bg-teal-50 text-teal-600 border border-teal-100/50"],
    webhook: ["Webhook", "bg-rose-50 text-rose-600 border border-rose-100/50"],
    zendesk: ["Zendesk", "bg-orange-50 text-orange-600 border border-orange-100/50"],
  };
  const [label, cls] = map[c] || ["Widget Chat", "bg-blue-50 text-blue-600 border border-blue-100/50"];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`} style={{ whiteSpace: "nowrap" }}>{label}</span>;
}

export default function Segments() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  // ── Segments State ──────────────────────────────────────────────────────────
  const [segments, setSegments] = useState([]);
  const [segmentsLoading, setSegmentsLoading] = useState(true);

  // Add / Edit Segment form state
  const [mode, setMode] = useState("idle"); // "idle" | "add" | "edit"
  const [editingSegment, setEditingSegment] = useState(null);
  const [segmentName, setSegmentName] = useState("");
  const [segmentDesc, setSegmentDesc] = useState("");
  const [segmentColor, setSegmentColor] = useState("#83bbe1");
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);

  // Detail drawer state (right-side drawer)
  const [detailSegment, setDetailSegment] = useState(null);
  const [detailContacts, setDetailContacts] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Delete Segment confirm state
  const [segDeleteModal, setSegDeleteModal] = useState({ open: false, segment: null, loading: false });

  // ── Contacts Right Panel State ──────────────────────────────────────────────
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactsError, setContactsError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [isChannelDropdownOpen, setIsChannelDropdownOpen] = useState(false);
  const channelDropdownRef = useRef(null);
  const itemsPerPage = 10;

  const activeAiAgentId = ApiClient.currentAiAgentId;

  // ── Close channel dropdown on outside click ─────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (channelDropdownRef.current && !channelDropdownRef.current.contains(e.target)) {
        setIsChannelDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Debounce search ─────────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ── Fetch Segments ──────────────────────────────────────────────────────────
  const fetchSegments = useCallback(async () => {
    setSegmentsLoading(true);
    try {
      const res = await ApiClient.getSegments();
      setSegments(res.segments || []);
    } catch {
      showToast("Failed to load segments", "error");
    } finally {
      setSegmentsLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchSegments(); }, [fetchSegments, activeAiAgentId]);

  // ── Fetch Contacts (right panel) ────────────────────────────────────────────
  const fetchContacts = useCallback(async (page = currentPage, search = debouncedSearch, channels = selectedChannels.join(",")) => {
    setContactsLoading(true);
    setContactsError(null);
    try {
      const res = await ApiClient.getContacts(page, itemsPerPage, search, channels);
      setContacts(res?.contacts || []);
      setTotalContacts(res?.total_count || 0);
    } catch {
      setContactsError("Failed to load contacts");
    } finally {
      setContactsLoading(false);
    }
  }, [currentPage, debouncedSearch, selectedChannels, itemsPerPage]);

  useEffect(() => {
    fetchContacts(currentPage, debouncedSearch, selectedChannels.join(","));
  }, [activeAiAgentId, currentPage, debouncedSearch, selectedChannels, fetchContacts]);

  // ── Segment Detail: load contacts ─────────────────────────────────────────
  const loadDetailContacts = useCallback(async (segment) => {
    if (!segment || !segment.contact_ids?.length) { setDetailContacts([]); return; }
    setDetailLoading(true);
    try {
      const res = await ApiClient.getContacts(1, 100);
      setDetailContacts((res?.contacts || []).filter(c => segment.contact_ids.includes(String(c.id))));
    } catch { setDetailContacts([]); }
    finally { setDetailLoading(false); }
  }, []);

  useEffect(() => {
    if (detailSegment) loadDetailContacts(detailSegment);
    else setDetailContacts([]);
  }, [detailSegment, loadDetailContacts]);

  // ── Segment CRUD handlers ───────────────────────────────────────────────────
  const startAdd = () => {
    setEditingSegment(null);
    setSegmentName("");
    setSegmentDesc("");
    setSegmentColor("#83bbe1");
    setSelectedContactIds(new Set());
    setDetailSegment(null);
    setMode("add");
  };

  const startEdit = (seg) => {
    setEditingSegment(seg);
    setSegmentName(seg.name);
    setSegmentDesc(seg.description || "");
    setSegmentColor(seg.color || "#83bbe1");
    setSelectedContactIds(new Set(seg.contact_ids.map(String)));
    setDetailSegment(null);
    setMode("edit");
  };

  const cancelMode = () => { setMode("idle"); setSelectedContactIds(new Set()); };

  const handleToggleContact = (contactId) => {
    const idStr = String(contactId);
    let transitionToAdd = false;
    setSelectedContactIds(prev => {
      const next = new Set(prev);
      if (next.has(idStr)) {
        next.delete(idStr);
      } else {
        next.add(idStr);
      }
      if (next.size > 0 && mode === "idle") {
        transitionToAdd = true;
      }
      return next;
    });
    if (transitionToAdd) {
      setMode("add");
    }
  };

  const handleSaveSegment = async () => {
    if (!segmentName.trim()) { showToast("Segment name is required", "warning"); return; }
    if (selectedContactIds.size === 0) {
      showToast("Please select at least one contact to create a segment", "warning");
      return;
    }
    setIsSaving(true);
    const payload = {
      name: segmentName.trim(),
      description: segmentDesc.trim() || null,
      color: segmentColor,
      contact_ids: Array.from(selectedContactIds)
    };
    try {
      if (mode === "edit") {
        await ApiClient.updateSegment(editingSegment.id, payload);
        showToast("Segment updated", "success");
      } else {
        await ApiClient.createSegment(payload);
        showToast("Segment created", "success");
      }
      setMode("idle");
      setSelectedContactIds(new Set());
      fetchSegments();
    } catch { showToast("Failed to save segment", "error"); }
    finally { setIsSaving(false); }
  };

  const confirmDeleteSegment = async () => {
    setSegDeleteModal(p => ({ ...p, loading: true }));
    try {
      await ApiClient.deleteSegment(segDeleteModal.segment.id);
      showToast("Segment deleted", "success");
      if (detailSegment?.id === segDeleteModal.segment.id) setDetailSegment(null);
      fetchSegments();
    } catch { showToast("Failed to delete segment", "error"); }
    finally { setSegDeleteModal({ open: false, segment: null, loading: false }); }
  };

  const startItem = totalContacts === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalContacts);

  const isAddEditMode = mode === "add" || mode === "edit";

  return (
    <>
      <style>{`
        .seg-root {
          display: flex;
          height: calc(100vh - 64px);
          background-color: #f4f6fa;
          overflow: hidden;
        }

        /* ── LEFT PANEL ── */
        .seg-left {
          width: 340px;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          background: #ffffff;
          border-right: 1px solid #e2e8f0;
          overflow: hidden;
        }
        .seg-left-header {
          padding: 16px 18px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }
        .seg-left-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
        }
        .seg-add-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 7px 12px;
          background-color: #83bbe1;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: background-color 0.15s;
        }
        .seg-add-btn:hover { background-color: #72a9cf; }
        .seg-add-btn.cancel { background-color: #f1f5f9; color: #475569; }
        .seg-add-btn.cancel:hover { background-color: #e2e8f0; }
        .seg-list {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .seg-card {
          padding: 12px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.15s;
          background: #ffffff;
        }
        .seg-card:hover { border-color: #cbd5e1; box-shadow: 0 2px 6px rgba(0,0,0,0.04); }
        .seg-card.active { border-color: #83bbe1; box-shadow: 0 0 0 2px rgba(131,187,225,0.2); }
        .seg-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .seg-card-name-row {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }
        .seg-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .seg-card-name {
          font-size: 13px;
          font-weight: 700;
          color: #0f172a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .seg-card-actions {
          display: flex;
          align-items: center;
          gap: 2px;
          flex-shrink: 0;
        }
        .seg-icon-btn {
          background: none;
          border: none;
          padding: 4px;
          border-radius: 5px;
          color: #94a3b8;
          cursor: pointer;
          display: flex;
          align-items: center;
          transition: all 0.12s;
        }
        .seg-icon-btn:hover { background: #f1f5f9; color: #475569; }
        .seg-icon-btn.danger:hover { background: #fee2e2; color: #ef4444; }
        .seg-card-desc {
          font-size: 11.5px;
          color: #64748b;
          margin-top: 4px;
          margin-left: 18px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .seg-card-count {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: 8px;
          margin-left: 18px;
          font-size: 11px;
          font-weight: 600;
          color: #83bbe1;
        }
        .seg-empty {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 32px;
          text-align: center;
          color: #94a3b8;
        }

        /* ── RIGHT PANEL ── */
        .seg-right {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: #ffffff;
          position: relative;
        }

        /* ── CONTACTS TABLE (Right Panel Body) ── */
        .contacts-panel-wrapper {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .table-toolbar {
          padding: 14px 18px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
          gap: 16px;
          flex-wrap: wrap;
        }
        .toolbar-left { display: flex; align-items: center; gap: 12px; }
        .toolbar-right { display: flex; align-items: center; gap: 8px; }
        .search-input-wrapper { position: relative; display: flex; align-items: center; }
        .search-icon { position: absolute; left: 10px; color: #94a3b8; }
        .search-field {
          padding: 8px 12px 8px 32px;
          font-size: 13px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          width: 210px;
          color: #0f172a;
          background-color: #f4f6fa;
        }
        .search-field:focus {
          outline: none;
          border-color: #83bbe1;
          background-color: #ffffff;
          box-shadow: 0 0 0 3px rgba(131,187,225,0.15);
        }
        .pagination-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
          border-left: 1px solid #e2e8f0;
          padding-left: 12px;
        }
        .pagination-text { font-size: 12px; color: #475569; font-weight: 500; }
        .pagination-actions { display: flex; gap: 4px; }
        .pagination-arrow-btn {
          background: none;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #475569;
          cursor: pointer;
        }
        .pagination-arrow-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .pagination-arrow-btn:not(:disabled):hover { border-color: #cbd5e1; color: #0f172a; }
        .table-overflow-container { flex: 1; overflow: auto; }
        .contacts-data-table { width: 100%; border-collapse: collapse; text-align: left; }
        .contacts-data-table th {
          padding: 10px 16px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          color: #475569;
          background-color: #f8fafc;
          border-bottom: 1.5px solid #e2e8f0;
          position: sticky;
          top: 0;
          z-index: 2;
        }
        .contacts-data-table td {
          padding: 12px 16px;
          font-size: 13px;
          color: #0f172a;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: middle;
        }
        .contact-row { transition: background-color 0.12s; cursor: pointer; }
        .contact-row:hover { background-color: #f8fafc; }
        .contact-row.row-selected { background-color: #f0f9ff; }
        .contact-row.row-in-segment { background-color: rgba(131,187,225,0.06); }
        .contact-cell { display: flex; align-items: center; gap: 10px; }
        .avatar-wrapper { position: relative; width: 32px; height: 32px; flex-shrink: 0; }
        .contact-avatar-img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
        .contact-name { font-weight: 600; color: #0f172a; }
        .empty-value { color: #94a3b8; }
        .email-link { color: #0284c7; text-decoration: none; font-weight: 500; }
        .email-link:hover { text-decoration: underline; }
        .custom-checkbox {
          width: 15px; height: 15px;
          border: 1.5px solid #94a3b8;
          border-radius: 4px;
          accent-color: #83bbe1;
          cursor: pointer;
        }
        .custom-checkbox.segment-check { accent-color: #83bbe1; }
        .col-checkbox { width: 40px; text-align: center; }
        .col-contact { min-width: 160px; }
        .col-channel { width: 110px; }
        .col-email { min-width: 160px; }
        .col-actions { width: 50px; text-align: center; }
        .action-menu-container { position: relative; display: inline-block; }
        .row-action-trigger {
          background: none; border: none; color: #94a3b8;
          padding: 5px; border-radius: 5px;
          display: flex; align-items: center; cursor: pointer;
        }
        .row-action-trigger:hover { background-color: #e2e8f0; color: #0f172a; }
        .menu-backdrop { position: fixed; inset: 0; z-index: 100; }
        .row-action-menu {
          position: absolute; right: 100%; top: 0;
          background: #fff; border: 1px solid #e2e8f0;
          border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
          padding: 4px; z-index: 101; min-width: 140px; margin-right: 4px;
        }
        .menu-item-btn {
          width: 100%; background: none; border: none;
          padding: 8px 10px; font-size: 12px; font-weight: 550;
          color: #475569; border-radius: 6px;
          display: flex; align-items: center; gap: 8px; text-align: left; cursor: pointer;
        }
        .menu-item-btn:hover { background-color: #f4f6fa; color: #0f172a; }
        .menu-separator { margin: 4px 0; border: 0; border-top: 1px solid #e2e8f0; }
        .table-empty-row { text-align: center; padding: 40px !important; color: #475569; }

        /* Shimmer */
        @keyframes shimmer {
          0% { background-position: -600px 0; }
          100% { background-position: 600px 0; }
        }
        .skeleton-cell {
          height: 14px; border-radius: 6px;
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
          background-size: 600px 100%; animation: shimmer 1.4s ease-in-out infinite;
        }
        .skeleton-avatar {
          width: 32px; height: 32px; border-radius: 50%;
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
          background-size: 600px 100%; animation: shimmer 1.4s ease-in-out infinite; flex-shrink: 0;
        }
        .skeleton-row td { padding: 14px 16px; border-bottom: 1px solid #f1f5f9; }

        /* ── Right panel top bar (mode indicator) ── */
        .seg-right-header {
          padding: 14px 18px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }
        .seg-right-title { font-size: 13px; font-weight: 700; color: #0f172a; }
        .seg-right-hint { font-size: 11.5px; color: #64748b; margin-top: 2px; }
        .seg-save-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          background: #ffffff;
          border-top: 1px solid #e2e8f0;
          flex-shrink: 0;
        }
        .seg-form-inline {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 1;
          flex-wrap: wrap;
        }
        .seg-form-input {
          padding: 7px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 13px;
          color: #0f172a;
          background: #f8fafc;
          flex: 1;
          min-width: 140px;
        }
        .seg-form-input:focus { outline: none; border-color: #83bbe1; background: #fff; }
        .seg-form-input.desc { flex: 1.5; }
        .seg-color-row { display: flex; align-items: center; gap: 5px; }
        .seg-swatch {
          width: 20px; height: 20px; border-radius: 50%;
          border: 2px solid transparent;
          cursor: pointer; transition: transform 0.1s;
          display: flex; align-items: center; justify-content: center;
        }
        .seg-swatch:hover { transform: scale(1.15); }
        .seg-swatch.sel { border-color: #0f172a; }
        .btn-seg-save {
          padding: 7px 16px;
          background: #83bbe1;
          color: #fff;
          border: none;
          border-radius: 8px;
          font-size: 12.5px;
          font-weight: 700;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.15s;
        }
        .btn-seg-save:hover { background: #72a9cf; }
        .btn-seg-save:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-seg-cancel {
          padding: 7px 14px;
          background: none;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 12.5px;
          font-weight: 600;
          color: #475569;
          cursor: pointer;
          transition: background 0.15s;
        }
        .btn-seg-cancel:hover { background: #f1f5f9; }
        .seg-count-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          font-weight: 600;
          background: #eff6ff;
          color: #83bbe1;
          border: 1px solid #dbeafe;
          padding: 3px 8px;
          border-radius: 20px;
          white-space: nowrap;
        }

        /* ── Detail Drawer (right-side half overlay) ── */
        .seg-detail-overlay {
          position: absolute;
          inset: 0;
          z-index: 20;
          pointer-events: none;
        }
        .seg-detail-backdrop {
          position: absolute;
          inset: 0;
          background: rgba(15, 23, 42, 0.25);
          pointer-events: auto;
          animation: fadeInBd 0.2s ease;
        }
        @keyframes fadeInBd { from { opacity: 0; } to { opacity: 1; } }
        .seg-detail-drawer {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          width: 52%;
          background: #ffffff;
          border-left: 1px solid #e2e8f0;
          box-shadow: -8px 0 24px rgba(0,0,0,0.1);
          display: flex;
          flex-direction: column;
          pointer-events: auto;
          animation: slideInDrawer 0.25s cubic-bezier(0.16,1,0.3,1);
          overflow: hidden;
        }
        @keyframes slideInDrawer {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .drawer-header {
          padding: 16px 20px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }
        .drawer-title-area { display: flex; align-items: center; gap: 10px; }
        .drawer-title { font-size: 15px; font-weight: 700; color: #0f172a; }
        .drawer-desc { font-size: 12px; color: #64748b; margin-top: 2px; }
        .drawer-close {
          background: none; border: none; cursor: pointer;
          color: #94a3b8; padding: 5px; border-radius: 6px;
          display: flex; align-items: center;
        }
        .drawer-close:hover { background: #f1f5f9; color: #475569; }
        .drawer-body { flex: 1; overflow-y: auto; }
        .drawer-contacts-table { width: 100%; border-collapse: collapse; }
        .drawer-contacts-table th {
          padding: 10px 16px; font-size: 11px; font-weight: 700; text-transform: uppercase;
          color: #475569; background: #f8fafc; border-bottom: 1.5px solid #e2e8f0;
          position: sticky; top: 0; z-index: 2;
        }
        .drawer-contacts-table td {
          padding: 11px 16px; font-size: 13px; color: #0f172a;
          border-bottom: 1px solid #f1f5f9; vertical-align: middle;
        }
        .drawer-empty {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; padding: 60px; color: #94a3b8; text-align: center;
        }
        .drawer-stats {
          padding: 16px 20px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          gap: 16px;
          flex-shrink: 0;
        }
        .stat-chip {
          display: flex; flex-direction: column; gap: 2px;
        }
        .stat-chip-label { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; }
        .stat-chip-value { font-size: 15px; font-weight: 800; color: #0f172a; }
      `}</style>

      <div className="seg-root">

        {/* ════════════════ LEFT PANEL: Segments List ════════════════ */}
        <div className="seg-left">
          <div className="seg-left-header">
            <div className="seg-left-title">
              <Layers size={16} color="#83bbe1" />
              <span>SEGMENTS</span>
            </div>
            {!isAddEditMode ? (
              <button className="seg-add-btn" onClick={startAdd}>
                <Plus size={13} /> Add Segment
              </button>
            ) : (
              <button className="seg-add-btn cancel" onClick={cancelMode}>
                <X size={13} /> Cancel
              </button>
            )}
          </div>

          <div className="seg-list">
            {segmentsLoading ? (
              <div style={{ textAlign: "center", padding: "32px", color: "#94a3b8" }}>
                <div style={{ width: "24px", height: "24px", border: "2px solid #e2e8f0", borderTopColor: "#83bbe1", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 8px" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <p style={{ fontSize: "12px" }}>Loading segments…</p>
              </div>
            ) : segments.length === 0 ? (
              <div className="seg-empty">
                <Layers size={32} color="#e2e8f0" />
                <p style={{ fontSize: "13px", marginTop: "12px", fontWeight: 600 }}>No segments yet</p>
                <p style={{ fontSize: "12px", marginTop: "4px" }}>Click "Add Segment" to get started</p>
              </div>
            ) : (
              segments.map(seg => (
                <div
                  key={seg.id}
                  className={`seg-card ${detailSegment?.id === seg.id ? "active" : ""}`}
                  onClick={() => { if (!isAddEditMode) setDetailSegment(seg); }}
                >
                  <div className="seg-card-top">
                    <div className="seg-card-name-row">
                      <div className="seg-dot" style={{ backgroundColor: seg.color || "#83bbe1" }} />
                      <span className="seg-card-name">{seg.name}</span>
                    </div>
                    <div className="seg-card-actions">
                      <button className="seg-icon-btn" onClick={(e) => { e.stopPropagation(); startEdit(seg); }} title="Edit">
                        <Edit2 size={13} />
                      </button>
                      <button className="seg-icon-btn danger" onClick={(e) => { e.stopPropagation(); setSegDeleteModal({ open: true, segment: seg, loading: false }); }} title="Delete">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  {seg.description && <div className="seg-card-desc">{seg.description}</div>}
                  <div className="seg-card-count">
                    <Users size={11} /> {seg.contact_ids?.length || 0} Contact{seg.contact_ids?.length !== 1 ? "s" : ""}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ════════════════ RIGHT PANEL: Contacts Table ════════════════ */}
        <div className="seg-right">

          {/* Contacts Table */}
          <div className="contacts-panel-wrapper" style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>

            {/* Toolbar */}
            <div className="table-toolbar">
              <div className="toolbar-left">
                <div className="search-input-wrapper">
                  <Search size={15} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search all contacts"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="search-field"
                  />
                </div>

                {/* Channel Filter */}
                <div ref={channelDropdownRef} style={{ position: "relative", width: "150px", zIndex: 10 }}>
                  <div
                    onClick={() => setIsChannelDropdownOpen(!isChannelDropdownOpen)}
                    style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "12px", fontWeight: 500, backgroundColor: "#f8fafc", color: "#1e293b", cursor: "pointer", display: "flex", justifycontent: "space-between", alignItems: "center", height: "36px" }}
                  >
                    <span style={{ fontSize: "11.5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "110px" }}>
                      {selectedChannels.length === 0 ? "All Channels" : selectedChannels.length === 1 ? channelOptions.find(o => o.value === selectedChannels[0])?.label : `${selectedChannels.length} Selected`}
                    </span>
                    <ChevronDown size={13} style={{ transform: isChannelDropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", color: "#64748b" }} />
                  </div>
                  {isChannelDropdownOpen && (
                    <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 100, maxHeight: "220px", overflowY: "auto" }}>
                      {channelOptions.map(opt => {
                        const isSel = opt.value === "" ? selectedChannels.length === 0 : selectedChannels.includes(opt.value);
                        return (
                          <div key={opt.value} onClick={() => {
                            if (opt.value === "") { setSelectedChannels([]); }
                            else {
                              const next = [...selectedChannels];
                              const idx = next.indexOf(opt.value);
                              idx > -1 ? next.splice(idx, 1) : next.push(opt.value);
                              setSelectedChannels(next);
                            }
                            setCurrentPage(1);
                          }} style={{ padding: "8px 12px", fontSize: "12px", color: isSel ? "#2563eb" : "#334155", backgroundColor: isSel ? "#eff6ff" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                            <input type="checkbox" checked={isSel} readOnly style={{ pointerEvents: "none" }} />
                            <span>{opt.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="toolbar-right">
                <div className="pagination-wrapper">
                  <span className="pagination-text">{startItem}–{endItem} of {totalContacts}</span>
                  <div className="pagination-actions">
                    <button className="pagination-arrow-btn" disabled={currentPage === 1 || contactsLoading} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}><ChevronLeft size={16} /></button>
                    <button className="pagination-arrow-btn" disabled={currentPage >= Math.ceil(totalContacts / itemsPerPage) || contactsLoading} onClick={() => setCurrentPage(p => p + 1)}><ChevronRight size={16} /></button>
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="table-overflow-container">
              {contactsError ? (
                <div style={{ padding: "40px", textAlign: "center", color: "#ef4444", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                  <AlertCircle size={28} /><p>{contactsError}</p>
                </div>
              ) : (
                <table className="contacts-data-table">
                  <thead>
                    <tr>
                      <th className="col-checkbox">
                        <input
                          type="checkbox"
                          className="custom-checkbox segment-check"
                          checked={contacts.length > 0 && contacts.every(c => selectedContactIds.has(String(c.id)))}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelectedContactIds(prev => {
                              const next = new Set(prev);
                              contacts.forEach(c => {
                                if (checked) {
                                  next.add(String(c.id));
                                } else {
                                  next.delete(String(c.id));
                                }
                              });
                              return next;
                            });
                            if (checked && mode === "idle") {
                              setMode("add");
                            }
                          }}
                          title="Select all on this page"
                        />
                      </th>
                      <th className="col-contact">Contact</th>
                      <th style={{ width: "150px" }}>Username</th>
                      <th className="col-channel">Channel</th>
                      <th className="col-email">Email</th>
                      <th style={{ width: "130px" }}>Phone</th>
                      <th className="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactsLoading ? (
                      Array.from({ length: 8 }).map((_, i) => (
                        <tr key={i} className="skeleton-row" style={{ opacity: 1 - i * 0.07 }}>
                          <td className="col-checkbox"><div className="skeleton-cell" style={{ width: "16px", height: "16px", borderRadius: "4px", margin: "0 auto" }} /></td>
                          <td><div style={{ display: "flex", alignItems: "center", gap: "10px" }}><div className="skeleton-avatar" /><div className="skeleton-cell" style={{ width: `${70 + i * 12}px` }} /></div></td>
                          <td><div className="skeleton-cell" style={{ width: `${50 + i * 10}px` }} /></td>
                          <td><div className="skeleton-cell" style={{ width: "60px", height: "20px", borderRadius: "20px" }} /></td>
                          <td><div className="skeleton-cell" style={{ width: `${80 + i * 8}px` }} /></td>
                          <td><div className="skeleton-cell" style={{ width: "90px" }} /></td>
                          <td />
                        </tr>
                      ))
                    ) : contacts.length === 0 ? (
                      <tr><td colSpan="7" className="table-empty-row">No contacts match your search.</td></tr>
                    ) : (
                      contacts.map(contact => {
                        const params = typeof contact.custom_parameters === "string" ? JSON.parse(contact.custom_parameters || "{}") : (contact.custom_parameters || {});
                        const imgUrl = params?.avatar_url
                          ? `${(process.env.REACT_APP_API_BASE_URL || "").replace(/\/api$/, "")}${params.avatar_url}`
                          : `https://api.dicebear.com/7.x/micah/svg?seed=${params?.avatar_seed || contact.name || contact.userid || String(contact.id)}&backgroundColor=b6e3f4,c0aede,d1d4f9`;
                        const uid = contact.userid;
                        const uuidReg = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                        const displayUid = !uid || uid === "anonymous" || uuidReg.test(uid) || /^(widget|telegram|slack|freshchat|freshdesk|zendesk|email|whatsapp|webchat)_/i.test(uid) ? "anonymous" : uid;
                        const em = contact.email;
                        const ph = contact.phone;
                        const isChecked = selectedContactIds.has(String(contact.id));

                        return (
                          <tr
                            key={contact.id}
                            className={`contact-row ${isChecked ? "row-selected" : ""}`}
                            onClick={() => {
                              if (isAddEditMode) {
                                handleToggleContact(contact.id);
                              } else {
                                navigate(`/contacts/${contact.id}`);
                              }
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <td className="col-checkbox">
                              <input
                                type="checkbox"
                                className="custom-checkbox segment-check"
                                checked={isChecked}
                                onChange={() => handleToggleContact(contact.id)}
                                onClick={e => e.stopPropagation()}
                              />
                            </td>
                            <td className="col-contact">
                              <div className="contact-cell">
                                <div className="avatar-wrapper">
                                  <img src={imgUrl} alt="Avatar" className="contact-avatar-img" />
                                </div>
                                <span className="contact-name">{contact.name}</span>
                              </div>
                            </td>
                            <td>
                              <span style={{ fontFamily: "monospace", fontSize: "12px", color: "#475569" }}>{displayUid}</span>
                            </td>
                            <td className="col-channel">
                              {getChannelBadge(params?.channel)}
                            </td>
                            <td className="col-email">
                              {!em || em === "N/A" ? <span className="empty-value">--</span> : <a href={`mailto:${em}`} className="email-link" onClick={e => e.stopPropagation()}>{em}</a>}
                            </td>
                            <td>
                              {!ph || ph === "N/A" ? <span className="empty-value">--</span> : ph}
                            </td>
                            <td className="col-actions">
                              {/* no nav actions in Segments view */}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Save bar (shown when in add/edit mode) */}
          {isAddEditMode && (
            <div className="seg-save-bar">
              <div className="seg-form-inline">
                <input
                  className="seg-form-input"
                  placeholder="Segment name *"
                  value={segmentName}
                  onChange={e => setSegmentName(e.target.value)}
                />
                <input
                  className="seg-form-input desc"
                  placeholder="Description (optional)"
                  value={segmentDesc}
                  onChange={e => setSegmentDesc(e.target.value)}
                />
                <div className="seg-color-row">
                  {COLOR_SWATCHES.map(c => (
                    <div
                      key={c}
                      className={`seg-swatch ${segmentColor === c ? "sel" : ""}`}
                      style={{ backgroundColor: c }}
                      onClick={() => setSegmentColor(c)}
                    >
                      {segmentColor === c && <Check size={10} color="#fff" />}
                    </div>
                  ))}
                </div>
                <span className="seg-count-chip"><Users size={11} /> {selectedContactIds.size} selected</span>
              </div>
              <button className="btn-seg-cancel" onClick={cancelMode}>Cancel</button>
              <button
                className="btn-seg-save"
                onClick={selectedContactIds.size === 0 || isSaving ? undefined : handleSaveSegment}
                title={selectedContactIds.size === 0 ? "Select at least one contact" : ""}
                style={{
                  opacity: selectedContactIds.size === 0 ? 0.6 : 1,
                  cursor: selectedContactIds.size === 0 ? "not-allowed" : "pointer"
                }}
              >
                {isSaving ? "Saving…" : mode === "edit" ? "Save Changes" : "Create Segment"}
              </button>
            </div>
          )}

          {/* ── Segment Detail Drawer ── */}
          {detailSegment && !isAddEditMode && (
            <div className="seg-detail-overlay">
              <div className="seg-detail-backdrop" onClick={() => setDetailSegment(null)} />
              <div className="seg-detail-drawer">
                <div className="drawer-header">
                  <div className="drawer-title-area">
                    <div style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: detailSegment.color || "#83bbe1", flexShrink: 0 }} />
                    <div>
                      <div className="drawer-title">{detailSegment.name}</div>
                      {detailSegment.description && <div className="drawer-desc">{detailSegment.description}</div>}
                    </div>
                  </div>
                  <button className="drawer-close" onClick={() => setDetailSegment(null)}>
                    <X size={17} />
                  </button>
                </div>
                <div className="drawer-stats">
                  <div className="stat-chip">
                    <span className="stat-chip-label">Total Contacts</span>
                    <span className="stat-chip-value">{detailContacts.length}</span>
                  </div>
                  <div className="stat-chip" style={{ borderLeft: "1px solid #e2e8f0", paddingLeft: "16px" }}>
                    <span className="stat-chip-label">Segment Color</span>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "2px" }}>
                      <div style={{ width: "14px", height: "14px", borderRadius: "50%", backgroundColor: detailSegment.color || "#83bbe1" }} />
                      <span style={{ fontSize: "12px", color: "#475569", fontWeight: 600 }}>
                        {COLOR_NAMES[detailSegment.color || "#83bbe1"] || "Sky Blue"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="drawer-body">
                  {detailLoading ? (
                    <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                      <div style={{ width: "24px", height: "24px", border: "2px solid #e2e8f0", borderTopColor: "#83bbe1", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 8px" }} />
                      <p style={{ fontSize: "12px" }}>Loading members…</p>
                    </div>
                  ) : detailContacts.length === 0 ? (
                    <div className="drawer-empty">
                      <Users size={32} color="#e2e8f0" />
                      <p style={{ fontSize: "13px", marginTop: "12px", fontWeight: 600 }}>No contacts in this segment</p>
                      <p style={{ fontSize: "12px", marginTop: "4px" }}>Edit the segment to add contacts.</p>
                    </div>
                  ) : (
                    <table className="drawer-contacts-table">
                      <thead>
                        <tr>
                          <th className="col-contact">Contact</th>
                          <th>Channel</th>
                          <th>Email</th>
                          <th>Phone</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailContacts.map(c => {
                          const params = typeof c.custom_parameters === "string" ? JSON.parse(c.custom_parameters || "{}") : (c.custom_parameters || {});
                          const imgUrl = params?.avatar_url
                            ? `${(process.env.REACT_APP_API_BASE_URL || "").replace(/\/api$/, "")}${params.avatar_url}`
                            : `https://api.dicebear.com/7.x/micah/svg?seed=${params?.avatar_seed || c.name || c.userid || String(c.id)}&backgroundColor=b6e3f4,c0aede,d1d4f9`;
                          const em = c.email;
                          const ph = c.phone;
                          return (
                            <tr key={c.id} onClick={() => navigate(`/contacts/${c.id}`)} className="contact-row" style={{ cursor: "pointer" }}>
                              <td>
                                <div className="contact-cell">
                                  <div className="avatar-wrapper"><img src={imgUrl} alt="Avatar" className="contact-avatar-img" /></div>
                                  <span className="contact-name">{c.name}</span>
                                </div>
                              </td>
                              <td>{getChannelBadge(params?.channel)}</td>
                              <td>{!em || em === "N/A" ? <span className="empty-value">--</span> : <a href={`mailto:${em}`} className="email-link">{em}</a>}</td>
                              <td>{!ph || ph === "N/A" ? <span className="empty-value">--</span> : ph}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Delete Segment Confirm ── */}
      <DeleteConfirmModal
        isOpen={segDeleteModal.open}
        onClose={() => setSegDeleteModal({ open: false, segment: null, loading: false })}
        onConfirm={confirmDeleteSegment}
        title="Delete Segment"
        message={`Are you sure you want to delete "${segDeleteModal.segment?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={segDeleteModal.loading}
      />
    </>
  );
}
